import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import {
  Button,
  ScrollArea,
  SearchInput,
  Select,
  Spinner,
} from '@mm/components';
import classNames from 'classnames';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COLLECTION_OPTIONS,
  QUALIFYING_QUESTION_OPTIONS,
  TemplateParams,
  TRIGGER_DEFINITIONS,
  TriggerType,
  slideoutBannersByTriggerType,
} from '../TriggersDefinitions';
import css from './InputTriggerModal.module.css';
import { TabBar } from '@mm/components/src/TabBar/TabBar';
import { useDebounce } from '@mm/hooks';
import { useAutomations } from '../../../use-automations';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { getPromoterOrTrigger } from '@mm/state/actions/automationActions';
import {
  getInputTriggerName,
  getInputTriggerPageId,
} from '../use-input-triggers-identification';
import { CampaignsOverviewPayload } from '@mm/state/model/CampaignOverviewModel';

type Props = {
  campaignId: number;
  className?: string;
  triggerType: TriggerType;
  onClose?: () => void;
  onBack?: () => void;
  onSubmit: (triggerType: TriggerType, params: TemplateParams) => Promise<void>;
};

const COMMON_SELECT_OPTIONS = {
  hideCount: true,
  hideClearButton: true,
  disableClickOutsideExceptions: true,
  staticDropdownPositioning: true,
  className: css.select,
  label: '',
  placeholder: 'Selection Required',
};

const TABS = {
  create: {
    key: 'create',
    label: 'Create new',
  },
  reuse: {
    key: 'reuse',
    label: 'Re-use Existing',
  },
};

const tabBarCss = {
  container: css.tabs,
  tab: css.tab,
};

export const InputTriggerModal: React.FC<Props> = ({
  campaignId,
  className,
  triggerType,
  onClose = () => {},
  onBack = () => {},
  onSubmit = async () => {},
}) => {
  const trigger = TRIGGER_DEFINITIONS[triggerType];

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const [selectedTab, setSelectedTab] = useState<string>(TABS.create.key);

  const [search, setSearch] = useState('');

  const [groupsState, setGroupsState] = useState<Record<number, boolean>>({});
  const [groupsBusyState, setGroupsBusyState] = useState<
    Record<number, boolean>
  >({});

  const [groupsOpts, setGroupsOpts] = useState<
    Record<number, { id: number; pageId: number; title: string }[]>
  >({});

  const q = useDebounce(search, 300);

  const [selectedOptionId, setSelectedOptionId] = useState<number>();
  const [selectedPageId, setSelectedPageId] = useState<number>();

  const [automations, setAutomations] = useState<CampaignsOverviewPayload>();
  const [showExplainer, setShowExplainer] = useState(false);

  const { fetchAutomations } = useAutomations();

  const list = useMemo(() => {
    return automations?.campaigns || [];
  }, [automations?.campaigns]);

  const filteredList = useMemo(() => {
    const byName = q
      ? v => v.name.trim().toLowerCase().includes(q.trim().toLowerCase())
      : () => true;

    return list.filter(byName);
  }, [q, list, campaignId]);

  const onGroupSelect = async (id: number) => {
    setGroupsState({
      ...groupsState,
      [id]: !groupsState[id],
    });

    // don't fetch on group collapse
    if (groupsState[id]) {
      return;
    }

    setGroupsBusyState({
      ...groupsBusyState,
      [id]: true,
    });

    try {
      const campaign = await campaignCRUDActions.retrieve(id);
      if (campaign) {
        setGroupsOpts({
          ...groupsOpts,
          [id]:
            campaign.campaignAudiencesAttributes?.reduce((a, v) => {
              const source = getPromoterOrTrigger(v);

              if (!source) {
                return a;
              }

              const pageId = getInputTriggerPageId(source);

              if (!pageId) {
                return a;
              }

              const triggerName = getInputTriggerName(source);
              const count = a.filter(opt =>
                opt.title.includes(triggerName),
              ).length;
              const title = triggerName + (count ? ` (${count + 1})` : '');

              const opt = {
                id: v.audienceId,
                pageId,
                title,
              };

              return [...a, opt];
            }, [] as { id: number; title: string; pageId: number }[]) || [],
        });
      }
    } finally {
      setGroupsBusyState({
        ...groupsBusyState,
        [id]: false,
      });
    }
  };

  const onOptionClick = (groupId: number, optionId: number, pageId: number) => {
    setSelectedOptionId(optionId);
    setSelectedPageId(pageId);
  };

  const [selectedCollectionOptions, setSelectedCollectionOptions] = useState<
    typeof COLLECTION_OPTIONS
  >([COLLECTION_OPTIONS[0], COLLECTION_OPTIONS[1]]);
  const [
    selectedQualifyingQuestionOptions,
    setSelectedQualifyingQuestionOptions,
  ] = useState<typeof QUALIFYING_QUESTION_OPTIONS>([
    QUALIFYING_QUESTION_OPTIONS[QUALIFYING_QUESTION_OPTIONS.length - 1],
  ]);

  const handleBack = () => {
    if (busy) {
      return;
    }
    onBack();
  };

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  const handleCreate = useCallback(async () => {
    try {
      setBusy(true);
      await onSubmit(triggerType, {
        collectionOptions: selectedCollectionOptions.map(option => option.id),
        qualifyingQuestions: selectedQualifyingQuestionOptions[0].id,
        inputTriggers: [{ kind: triggerType }],
        widgetsPreset: trigger.widgetsPreset
          ? trigger.widgetsPreset
          : undefined,
      });
    } catch (err) {
    } finally {
      setBusy(false);
    }
  }, [selectedCollectionOptions, selectedQualifyingQuestionOptions]);

  const handleAttach = async () => {
    try {
      setBusy(true);
      await onSubmit(triggerType, {
        inputTriggers: [{ pageId: selectedPageId, kind: triggerType }],
      });
    } catch (err) {
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (selectedTab === TABS.reuse.key && selectedOptionId && selectedPageId) {
      handleAttach();
    } else {
      handleCreate();
    }
  }, [
    onSubmit,
    selectedCollectionOptions,
    selectedQualifyingQuestionOptions,
    triggerType,
    trigger,
    selectedTab,
    selectedOptionId,
    selectedPageId,
  ]);

  const fetchMore = async () => {
    if (automations?.pagination && automations?.campaigns) {
      const res = await fetchAutomations({
        page: automations.pagination.page + 1,
        perPage: 50,
      });

      if (res) {
        setAutomations({
          campaigns: [...automations.campaigns, ...res.campaigns],
          pagination: res.pagination,
        });
      }
    }
  };

  useEffect(() => {
    (async () => {
      const res = await fetchAutomations({ perPage: 50 });
      if (res) {
        setAutomations(res);
      }
    })();
  }, []);

  const submitDisabled =
    (selectedTab === TABS.reuse.key && !selectedOptionId) || busy;

  if (!trigger.template) {
    return null;
  }

  return (
    <div className={classNames(className, css.container)}>
      <div className={css.modal}>
        <div className={css.header}>
          <span className={css.back} onClick={handleBack}>
            <ArrowLeftOutlined />
          </span>
          Input Triggers{' '}
          <span className={css.close} onClick={handleClose}>
            <CloseOutlined />
          </span>
        </div>
        <div
          className={classNames(css.content, {
            [css.increasedHeightContent]: selectedTab === TABS.reuse.key,
          })}
        >
          <div
            className={css.explanerButton}
            onMouseOver={() => setShowExplainer(true)}
            onFocus={() => setShowExplainer(true)}
            onTouchStart={() => setShowExplainer(true)}
            onMouseLeave={() => setShowExplainer(false)}
            onTouchEnd={() => setShowExplainer(false)}
          >
            How does it work?
          </div>

          {showExplainer && slideoutBannersByTriggerType(triggerType)}

          {!showExplainer && (
            <>
              <div className={css.contentHeader}>
                <div className={css.icon}>
                  <trigger.icon />
                </div>

                <div className={css.title}>{trigger.template.title}</div>
              </div>

              <TabBar<string>
                css={tabBarCss}
                selectedTab={selectedTab}
                onTabSelected={setSelectedTab}
                tabs={Object.values(TABS)}
              />

              {selectedTab === TABS.create.key && (
                <>
                  <div className={css.body}>
                    {trigger.template.body},
                    <p>
                      try to collect their
                      <Select
                        {...COMMON_SELECT_OPTIONS}
                        disabled={busy}
                        options={COLLECTION_OPTIONS}
                        selected={selectedCollectionOptions}
                        onChange={setSelectedCollectionOptions}
                      />{' '}
                      and ask{' '}
                      <Select
                        singleSelect
                        {...COMMON_SELECT_OPTIONS}
                        disabled={busy}
                        options={QUALIFYING_QUESTION_OPTIONS}
                        selected={selectedQualifyingQuestionOptions}
                        onChange={setSelectedQualifyingQuestionOptions}
                      />
                      qualifying{' '}
                      {pluralize(
                        'question',
                        selectedQualifyingQuestionOptions[0].id,
                      )}
                      .
                    </p>
                  </div>

                  <div className={css.helper}>
                    This data will be used in your re-engagement automation.
                  </div>
                </>
              )}

              {selectedTab === TABS.reuse.key && (
                <div className={classNames(css.body, css.reuse)}>
                  <p className={css.info}>
                    Link your new Input Trigger to the Message Content from an
                    existing one. Edits made to the new Input Trigger WILL
                    affect the original and vice versa.
                  </p>

                  <div className={css.list}>
                    <div className={css.search}>
                      <SearchInput
                        css={{ container: css.searchInput }}
                        placeholder="Search for automation"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                    </div>

                    <ScrollArea className={css.listResults}>
                      {filteredList.map(v => (
                        <React.Fragment key={v.id}>
                          <div
                            key={v.id + v.name + v.audienceId}
                            className={css.group}
                            onClick={() => onGroupSelect(v.id)}
                          >
                            <span className={css.groupTitle}>{v?.name}</span>
                            <span className={css.groupIcon}>
                              {groupsState[v.id] ? (
                                <MinusOutlined />
                              ) : (
                                <PlusOutlined />
                              )}
                            </span>
                          </div>
                          {groupsBusyState[v.id] && (
                            <Spinner className={css.loader} standalone />
                          )}

                          {groupsState[v.id] && (
                            <div className={css.groupList}>
                              {groupsOpts[v.id]?.map(opt => (
                                <div
                                  key={opt.id + opt.title + opt.pageId}
                                  className={classNames(css.groupListItem, {
                                    [css.groupListItemSelected]:
                                      selectedOptionId === opt.id,
                                  })}
                                  onClick={() =>
                                    onOptionClick(v.id, opt.id, opt.pageId)
                                  }
                                >
                                  {opt.title}
                                </div>
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                      {!!(
                        !automations?.pagination?.isLastPage &&
                        automations?.pagination?.page
                      ) && (
                        <div className={css.more}>
                          <Button
                            size="sm"
                            onClick={fetchMore}
                            color="transparent-no-shadow"
                          >
                            Load more
                          </Button>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              )}

              <Button
                css={{ container: css.submit }}
                onClick={handleSubmit}
                leftIcon={
                  busy ? (
                    <Spinner standalone size={16} />
                  ) : (
                    <ArrowRightOutlined />
                  )
                }
                disabled={submitDisabled}
              >
                Let's go
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
